import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, Landmark, Briefcase, Dumbbell, Heart, Music, TreePine,
  MapPin, Settings, BarChart3, ChevronRight, Loader2, Shield
} from "lucide-react";

const TYPE_ICONS = {
  PRIVATE_COMMERCIAL: Building2, CONDO: Building2, SUBDIVISION: TreePine,
  LGU_CITY: Landmark, LGU_BARANGAY: MapPin, CORPORATE_COMPLEX: Briefcase,
  GYM_FITNESS_CENTER: Dumbbell, YOGA_STUDIO: Heart, DANCE_STUDIO: Music,
};
const TYPE_COLORS = {
  PRIVATE_COMMERCIAL: "bg-blue-100 text-blue-800", CONDO: "bg-purple-100 text-purple-800",
  SUBDIVISION: "bg-green-100 text-green-800", LGU_CITY: "bg-amber-100 text-amber-800",
  LGU_BARANGAY: "bg-amber-100 text-amber-800", CORPORATE_COMPLEX: "bg-slate-100 text-slate-800",
  GYM_FITNESS_CENTER: "bg-red-100 text-red-800", YOGA_STUDIO: "bg-pink-100 text-pink-800",
  DANCE_STUDIO: "bg-violet-100 text-violet-800",
};

export default function VIGFDashboard() {
  const [overview, setOverview] = useState(null);
  const [venues, setVenues] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [classifyVenue, setClassifyVenue] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [selectedAccess, setSelectedAccess] = useState("PUBLIC");
  const [selectedRevenue, setSelectedRevenue] = useState("COMMISSION");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [ovRes, venRes, sessRes] = await Promise.all([
        api.get("/vigf/admin/overview"),
        api.get("/venues"),
        api.get("/vigf/activity-sessions?status=open"),
      ]);
      setOverview(ovRes.data);
      setVenues(venRes.data || []);
      setSessions(sessRes.data.sessions || []);
    } catch { toast.error("Failed to load VIGF data"); }
    setLoading(false);
  };

  const handleClassify = async () => {
    if (!classifyVenue || !selectedType) return;
    try {
      await api.post(`/vigf/classify/${classifyVenue.venue_id}`, {
        venue_type: selectedType, access_mode: selectedAccess, revenue_model: selectedRevenue,
      });
      toast.success(`Venue classified as ${selectedType}`);
      setClassifyVenue(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Classification failed"); }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const types = overview?.venue_types || {};
  const dist = overview?.type_distribution || {};

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6" data-testid="vigf-dashboard">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl">Venue Intelligence & Governance</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="classify">Classify Venues</TabsTrigger>
          <TabsTrigger value="activities">Activity Sessions</TabsTrigger>
          <TabsTrigger value="types">Venue Types</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold">{overview?.total_venues || 0}</p>
              <p className="text-xs text-muted-foreground">Total Venues</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-amber-500">{overview?.unclassified || 0}</p>
              <p className="text-xs text-muted-foreground">Unclassified</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold">{overview?.activity_sessions || 0}</p>
              <p className="text-xs text-muted-foreground">Activity Sessions</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold">{overview?.active_passes || 0}</p>
              <p className="text-xs text-muted-foreground">Active Passes</p>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Type Distribution</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {Object.entries(types).map(([key, info]) => {
                  const Icon = TYPE_ICONS[key] || Building2;
                  const count = dist[key] || 0;
                  return (
                    <div key={key} className="text-center p-3 rounded-lg border hover:border-primary/50 transition-colors">
                      <Icon className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-lg font-heading font-bold">{count}</p>
                      <p className="text-[9px] text-muted-foreground leading-tight">{info.label}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Classify Venues */}
        <TabsContent value="classify" className="space-y-3 mt-4">
          {venues.filter(v => !v.venue_type).length === 0 && venues.length > 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">All venues are classified</CardContent></Card>
          ) : null}
          {venues.map(v => (
            <Card key={v.venue_id} className="cursor-pointer hover:border-primary/50" onClick={() => { setClassifyVenue(v); setSelectedType(v.venue_type || ""); }}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-sm font-bold">{v.name}</h3>
                    {v.venue_type ? (
                      <Badge className={TYPE_COLORS[v.venue_type] || "bg-muted"}>{types[v.venue_type]?.label || v.venue_type}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">Unclassified</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{v.city} · {(v.sport_types || []).join(", ")}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Activity Sessions */}
        <TabsContent value="activities" className="space-y-3 mt-4">
          {sessions.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">No activity sessions yet</CardContent></Card>
          ) : sessions.map(s => (
            <Card key={s.session_id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-medium">{s.title}</h3>
                      <Badge variant="outline" className="text-[9px]">{s.session_type}</Badge>
                      <Badge variant="outline" className="text-[9px]">{s.skill_level}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{s.instructor_name} · {s.venue_name}</p>
                    <p className="text-xs text-muted-foreground">{s.start_datetime?.slice(0, 10)} {s.start_datetime?.slice(11, 16)} · {s.participants_count}/{s.max_capacity} joined</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm">₱{s.session_price}</p>
                    <Badge className={s.session_status === "open" ? "bg-green-100 text-green-800" : "bg-muted"}>{s.session_status}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Venue Types Reference */}
        <TabsContent value="types" className="space-y-3 mt-4">
          {Object.entries(types).map(([key, info]) => {
            const Icon = TYPE_ICONS[key] || Building2;
            return (
              <Card key={key}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${TYPE_COLORS[key] || "bg-muted"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-sm font-bold">{info.label}</h3>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(info.features || []).map(f => (
                          <Badge key={f} variant="outline" className="text-[8px]">{f.replace(/_/g, " ")}</Badge>
                        ))}
                      </div>
                    </div>
                    <Badge>{dist[key] || 0} venues</Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Classify Dialog */}
      <Dialog open={!!classifyVenue} onOpenChange={(v) => { if (!v) setClassifyVenue(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Classify: {classifyVenue?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium mb-1">Venue Type</p>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger data-testid="classify-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(types).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-medium mb-1">Access Mode</p>
              <Select value={selectedAccess} onValueChange={setSelectedAccess}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["PUBLIC","RESIDENT_ONLY","MEMBER_ONLY","CORPORATE_ONLY","MEMBERS_PLUS_GUEST","PUBLIC_DROP_IN"].map(m => (
                    <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-medium mb-1">Revenue Model</p>
              <Select value={selectedRevenue} onValueChange={setSelectedRevenue}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["COMMISSION","SUBSCRIPTION","HYBRID","REVENUE_SHARE","FLAT_FEE"].map(m => (
                    <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleClassify} className="w-full rounded-full" data-testid="classify-submit-btn">
              <Settings className="w-4 h-4 mr-2" />Classify & Apply Governance
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
